import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useGetTutorials } from '@/hooks/service-hooks/tutorials';
import { selectTutorialRemaining, useAuthStore } from '@/store';
import { useTourContext } from '@/store/tourContext';

const TourHandler = memo(() => {
	const user = useAuthStore((state) => state.user);
	const tutorialRemaining = useAuthStore(selectTutorialRemaining);
	const { startTour, isTourActive } = useTourContext();
	const { data: tutorials = [], isLoading: isLoadingTutorials } =
		useGetTutorials();
	const hasStartedTourRef = useRef(false);

	const userPermissions = useMemo(
		() => user?.userInformation?.permissions ?? [],
		[user?.userInformation?.permissions],
	);
	const userPort = user?.userInformation?.portsAvailable;

	const hasPermission = useCallback(
		(requiredPermissions?: string[]) => {
			if (!requiredPermissions || requiredPermissions.length === 0) {
				return true;
			}
			return requiredPermissions.every((permission) =>
				userPermissions.includes(permission),
			);
		},
		[userPermissions],
	);

	const matchesPort = useCallback(
		(tutorialPort?: string) => {
			if (!tutorialPort) {
				return true;
			}
			if (!userPort) {
				return false;
			}
			return (
				tutorialPort.toLowerCase().trim() === userPort.toLowerCase().trim()
			);
		},
		[userPort],
	);

	const onboardingTutorial = useMemo(() => {
		if (isLoadingTutorials || !tutorials || tutorials.length === 0) {
			return null;
		}

		const onboardingTutorialNames = [
			'OnboardingUser',
			'OnboardingWCTutorial',
			'OnboardingSalesRepTutorial',
			'OnboardingTutorialGraftLogs',
			'OnboardingTutorialPaymentMSL',
			'OnboardingMindsightTutorial',
		];

		return tutorials.find(
			(t) =>
				onboardingTutorialNames.includes(t.name) &&
				hasPermission(t.permissions) &&
				t.enabled !== false &&
				matchesPort(t.portsAvailable as string | undefined),
		);
	}, [tutorials, isLoadingTutorials, hasPermission, matchesPort]);

	useEffect(() => {
		if (
			!user ||
			tutorialRemaining === undefined ||
			tutorialRemaining <= 0 ||
			isTourActive ||
			hasStartedTourRef.current ||
			isLoadingTutorials ||
			!onboardingTutorial
		) {
			return;
		}

		const timer = setTimeout(() => {
			hasStartedTourRef.current = true;
			void startTour(onboardingTutorial.name);
		}, 1500);

		return () => clearTimeout(timer);
	}, [
		user,
		tutorialRemaining,
		isTourActive,
		startTour,
		isLoadingTutorials,
		onboardingTutorial,
	]);

	return null;
});

TourHandler.displayName = 'TourHandler';

export default TourHandler;
